import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton, Modal, Card, CardContent, CardMedia, CardActionArea, CardActions } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useGetItemsQuery, useDeleteItemMutation } from '../store/docApi';
import TripForm from './TripForm';

function Trips() {
  const navigate = useNavigate();
  const { data: trips, isLoading, error } = useGetItemsQuery({ label: 'trip' });
  const [deleteItem] = useDeleteItemMutation();
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDelete = async (id) => {
    try {
      await deleteItem({ label: 'trips', id }).unwrap();
    } catch (error) {
      console.error('Failed to delete trip:', error);
    }
  };

  const handleEdit = (trip) => {
    setSelectedTrip(trip);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedTrip(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTrip(null);
  };

  const handleTripClick = (pk, id) => {
    navigate(`/trips/${pk}/${id}`);
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading trips</Typography>;

  return (
    <Box sx={{ justifyContent: 'center' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Trips
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={handleAdd}>
          Add Trip
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          justifyContent: 'center'
        }}
      >
        {trips
          ?.slice()
          .sort((a, b) => b._ts - a._ts)
          .map((trip) => (
            <Card key={trip.id} onClick={() => handleTripClick(trip.pk, trip.id)} sx={{ width: 300 }}>
              <CardActionArea>
                {trip.picture && (
                  <CardMedia component="img" image={trip.picture} alt={trip.name} sx={{ height: 160, objectFit: 'cover' }} />
                )}
                <CardContent>
                  <Typography variant="h5">{trip.name}</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {trip.description}
                  </Typography>
                  {trip.startDate && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {new Date(trip.startDate).toLocaleDateString()} -{' '}
                      {trip.endDate ? new Date(trip.endDate).toLocaleDateString() : 'Ongoing'}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
              <CardActions sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(trip);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(trip.id);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))}
      </Box>

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: '90%',
            maxWidth: 500
          }}
        >
          <TripForm trip={selectedTrip} onClose={handleCloseModal} />
        </Box>
      </Modal>
    </Box>
  );
}

export default Trips;
